import React from 'react';
import { Column, SectionDivider } from '@ydistri/ds';
import ScopeEntitySelection from './ScopeEntitySelection';
import ScopeConfigurationView from './ScopeConfigurationView';
import { useScopeOptionsData } from './useScopeOptionsData';
import { isModalViewMode, ViewMode } from './scopeUtilsLib';
import { ConfigurationRuleScopeEntityType } from '@ydistri/api-sdk';
import ScopeMissingEntityView from './ScopeMissingEntityView';
import { useApplicationData } from '../../../hooks/useApplicationData';
import { TemplateOrCalculationWithValue } from '../../../apis/api';

interface ScopeDefinitionProps {
  scopeId: number;
  viewMode: ViewMode;
  disabled?: boolean;
  templateOrCalculation: TemplateOrCalculationWithValue;
}

const ScopeDefinition: React.FC<ScopeDefinitionProps> = ({
  scopeId,
  viewMode,
  disabled = false,
  templateOrCalculation,
}) => {
  const {
    regionOptions,
    storeOptions,
    allStoreOptions,
    storeTypeOptions,
    departmentOptions,
    loading,
  } = useScopeOptionsData(scopeId, viewMode, templateOrCalculation);
  const { departments: hasDepartments } = useApplicationData();

  const inModal = isModalViewMode(viewMode);

  return (
    <Column $gap="1rem">
      {inModal && <SectionDivider $modal />}
      <Column $gap="1rem" $alignItems="flex-end">
        <ScopeEntitySelection
          scopeId={scopeId}
          entityType={ConfigurationRuleScopeEntityType.Regions}
          options={regionOptions}
          loading={loading}
          viewMode={viewMode}
          disabled={disabled}
          templateOrCalculation={templateOrCalculation}
        />
        {inModal && <SectionDivider $modal />}
        <ScopeEntitySelection
          scopeId={scopeId}
          entityType={ConfigurationRuleScopeEntityType.StoreTypes}
          options={storeTypeOptions}
          loading={loading}
          viewMode={viewMode}
          disabled={disabled}
          templateOrCalculation={templateOrCalculation}
        />
        {inModal && <SectionDivider $modal />}
        <ScopeEntitySelection
          scopeId={scopeId}
          entityType={ConfigurationRuleScopeEntityType.Stores}
          options={storeOptions}
          dataCheck={allStoreOptions}
          dataCheckLabel="Outside of scope"
          loading={loading}
          viewMode={viewMode}
          disabled={disabled}
          templateOrCalculation={templateOrCalculation}
        />
        {inModal && <SectionDivider $modal />}
        {hasDepartments && (
          <ScopeEntitySelection
            scopeId={scopeId}
            entityType={ConfigurationRuleScopeEntityType.Departments}
            options={departmentOptions}
            loading={loading}
            viewMode={viewMode}
            disabled={disabled}
            templateOrCalculation={templateOrCalculation}
          />
        )}
        {viewMode === ViewMode.ADJUST_SCOPE_MODAL && (
          <ScopeMissingEntityView
            scopeId={scopeId}
            viewMode={viewMode}
            templateOrCalculation={templateOrCalculation}
          />
        )}
        <SectionDivider $modal={inModal} />
      </Column>
      {viewMode !== ViewMode.ADJUST_SCOPE_MODAL && (
        <ScopeConfigurationView
          scopeId={scopeId}
          viewMode={viewMode}
          templateOrCalculation={templateOrCalculation}
        />
      )}
    </Column>
  );
};

export default ScopeDefinition;
